/**
 * UTM parameter capture and retrieval utilities.
 *
 * Captures UTM parameters from the landing page URL and stores them in
 * sessionStorage so they persist across in-app navigation but clear
 * when the browser session ends.
 *
 * Used primarily to forward ad-attribution data to the Calendly embed.
 */

const UTM_STORAGE_KEY = 'therapylake_utm_params';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

/**
 * Reads UTM parameters from the current page URL and stores them in
 * sessionStorage.  Only writes when at least one UTM param is present.
 * Does NOT overwrite previously stored values — first-touch wins within
 * the same browser session.
 */
export const captureUtmParams = () => {
    // Don't overwrite if we already captured UTMs in this session
    if (sessionStorage.getItem(UTM_STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const utmParams = {};
    let hasAny = false;

    UTM_KEYS.forEach((key) => {
        const value = params.get(key);
        if (value) {
            utmParams[key] = value;
            hasAny = true;
        }
    });

    if (hasAny) {
        sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
    }
};

/**
 * Returns the stored UTM parameters formatted for the react-calendly
 * `utm` prop (camelCase keys):
 *
 *   { utmSource, utmMedium, utmCampaign, utmContent, utmTerm }
 *
 * Returns an empty object when no UTMs have been captured.
 */
export const getCalendlyUtmParams = () => {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};

    try {
        const stored = JSON.parse(raw);
        return {
            ...(stored.utm_source && { utmSource: stored.utm_source }),
            ...(stored.utm_medium && { utmMedium: stored.utm_medium }),
            ...(stored.utm_campaign && { utmCampaign: stored.utm_campaign }),
            ...(stored.utm_content && { utmContent: stored.utm_content }),
            ...(stored.utm_term && { utmTerm: stored.utm_term }),
        };
    } catch {
        return {};
    }
};
